/**
 * friendlyError — turn any thrown/rejected error into user-facing copy.
 *
 * Priority order:
 *   1. Backend's `response.data.detail` — our API endpoints already
 *      craft user-safe messages here ("Card is powering autopay",
 *      "This tier is locked", etc.).
 *   2. HTTP-status-based defaults for common cases (401/403/404/409/500).
 *   3. Offline detection.
 *   4. Generic "please try again" as the last resort.
 *
 * Callers pass the result straight into a toast description; the caller
 * chooses the toast title based on their own context.
 *
 *   toast({ title: 'Save failed', description: friendlyError(err) });
 */

const isOffline = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

// Support address shown in fallback copy. Callers may pass a custom one
// via options.supportEmail. Kept in sync with `usePlatformContact.js`.
const DEFAULT_SUPPORT_EMAIL = 'support@myhometowndirect.com';

const withSupport = (text, email = DEFAULT_SUPPORT_EMAIL) =>
  `${text} If it keeps happening, email ${email}.`;

const STATUS_FALLBACKS = {
  400: 'That request looks off — please double-check the details and try again.',
  401: 'Your session expired. Please log back in and try again.',
  403: 'You do not have permission for that action.',
  404: withSupport("We couldn't find what you were looking for — try refreshing the page."),
  409: 'That change conflicts with something already saved. Refresh and try again.',
  413: 'The file you uploaded is too big — try a smaller one.',
  422: 'Some of the information provided is invalid. Please review and try again.',
  429: "You're doing that a lot — give it a moment and try again.",
  500: withSupport("Something went wrong on our end. We've been notified — please try again in a minute."),
  502: withSupport('Our service is briefly unreachable. Please try again in a moment.'),
  503: withSupport('Our service is briefly unavailable. Please try again in a moment.'),
  504: withSupport('That took too long. Please try again in a moment.'),
};

// Tokens from axios / fetch that we should never surface to users.
const RAW_ERROR_TOKENS = [
  'request failed with status code',
  'network error',
  'axioserror',
  'typeerror',
  'referenceerror',
  'timeout of',
  'ecconnaborted',
  'json.parse',
  'unexpected token',
  '[object object]',
];

// Substrings that mean the backend detail is a SYSTEM-level error — not
// something the end user can act on. When any of these match, we swap
// the detail for the status-based fallback (which already tells the user
// how to reach support). Kept lowercase for case-insensitive matching.
const SYSTEM_DETAIL_TOKENS = [
  'not found',
  'does not exist',
  'no such',
  'internal server',
  'internal error',
  'server error',
  'database',
  'psycopg',
  'connection refused',
  'connection reset',
  'traceback',
  'traceid',
  'unhandled',
  'nonetype',
  'null value',
  'foreign key',
  'unique constraint',
  'duplicate key',
  'operationalerror',
  'integrityerror',
  'attributeerror',
  'keyerror',
  'could not delete',
  'still linked to another table',
];

const looksRaw = (msg) => {
  const s = String(msg || '').toLowerCase();
  return RAW_ERROR_TOKENS.some((tok) => s.includes(tok));
};

const looksLikeSystemError = (msg) => {
  const s = String(msg || '').toLowerCase();
  return SYSTEM_DETAIL_TOKENS.some((tok) => s.includes(tok));
};

export const friendlyError = (err, options = {}) => {
  const supportEmail = options.supportEmail || DEFAULT_SUPPORT_EMAIL;
  const finalize = (text) => text.replace(DEFAULT_SUPPORT_EMAIL, supportEmail);

  if (isOffline()) {
    return "You're offline — reconnect and try again. Any changes you made are still here.";
  }

  const status = err?.response?.status;

  // 1) Server-provided detail (our backend hardening scrubs 500 detail).
  //    Skip system-flavoured detail (e.g. "Employer not found",
  //    "Database connection refused") — those aren't actionable by
  //    the end user, so we fall through to the friendly status copy.
  const rawDetail = err?.response?.data?.detail;
  const detail = (rawDetail && typeof rawDetail === 'object')
    ? (rawDetail.reason || rawDetail.error || rawDetail.message || null)
    : rawDetail;
  if (detail && typeof detail === 'string'
      && !looksRaw(detail)
      && !looksLikeSystemError(detail)) {
    return detail;
  }
  // Some endpoints return { message: ... } or { error: ... }
  const message = err?.response?.data?.message || err?.response?.data?.error;
  if (message && typeof message === 'string'
      && !looksRaw(message)
      && !looksLikeSystemError(message)) {
    return message;
  }

  // 2) Status-based fallback.
  if (status && STATUS_FALLBACKS[status]) {
    return finalize(STATUS_FALLBACKS[status]);
  }
  // Non-2xx but no mapped status
  if (status && status >= 400 && status < 500) {
    return finalize(withSupport('That request could not be completed.', supportEmail));
  }
  if (status && status >= 500) {
    return finalize(STATUS_FALLBACKS[500]);
  }

  // 3) Network-level (no response at all — usually offline/DNS/CORS)
  if (err?.code === 'ERR_NETWORK' || /network/i.test(err?.message || '')) {
    return finalize(withSupport("We couldn't reach our servers.", supportEmail));
  }

  // 4) Generic last resort — never surface raw axios strings.
  return options.fallback
    || finalize(withSupport('Something went wrong.', supportEmail));
};

/**
 * friendlyTitle — pick a matching toast title for an error, so callers
 * don't have to hand-craft one every time.
 */
export const friendlyTitle = (err, defaultTitle = 'Something went wrong') => {
  const status = err?.response?.status;
  if (isOffline()) return "You're offline";
  if (status === 401) return 'Please log back in';
  if (status === 403) return 'Not allowed';
  if (status === 404) return 'Not found';
  if (status === 409) return 'Conflict';
  if (status === 429) return 'Slow down';
  if (status && status >= 500) return "We'll get this fixed";
  return defaultTitle;
};

export default friendlyError;
